<template>
  <div class="series-editor">
    <div class="series-editor__no" :style="{ background: value.color }">{{ index + 1 }}</div>
    <div class="form-row flex-grow-1">
      <div class="col-md-2 mb-2">
        <select class="form-control form-control-sm" :value="value.massId === null ? '' : value.massId" @change="setAsset($event.target.value)">
          <option value="" disabled>Vessel</option>
          <option v-for="asset in assets" :key="asset.massId" :value="asset.massId">{{ asset.massName }}</option>
        </select>
      </div>
      <div class="col-md-3 mb-2">
        <select class="form-control form-control-sm" :value="value.deviceUid" :disabled="!value.massId" @change="setDevice($event.target.value)">
          <option value="" disabled>Device</option>
          <option v-for="device in devices" :key="device.devcUniqueId" :value="device.devcUniqueId">
            {{ device.devcName }} ({{ device.devcUniqueId }})
          </option>
        </select>
        <small v-if="deviceMissing" class="text-danger">Device no longer available</small>
      </div>
      <div class="col-md-2 mb-2">
        <select class="form-control form-control-sm" :value="value.point" :disabled="!value.deviceUid" @change="update({ point: $event.target.value })">
          <option value="" disabled>Data point</option>
          <option v-for="p in points" :key="p.point" :value="p.point">{{ p.label }} ({{ p.unit }})</option>
        </select>
      </div>
      <div v-if="channelMode" class="col-md-1 mb-2">
        <select class="form-control form-control-sm" :value="value.channel || ''" :disabled="loadingChannels" @change="update({ channel: $event.target.value })">
          <option value="" disabled>{{ loadingChannels ? 'Loading…' : 'Channel' }}</option>
          <option v-for="c in channels" :key="c" :value="c">{{ c }}</option>
        </select>
        <small v-if="!loadingChannels && !recentChannels" class="text-muted">No recent data</small>
      </div>
      <div class="col-md-2 mb-2">
        <select class="form-control form-control-sm" :value="value.period.kind" @change="setPeriod($event.target.value)">
          <option v-for="p in periodKinds" :key="p.value" :value="p.value">{{ p.label }}</option>
        </select>
      </div>
      <div v-if="value.period.kind === 'from'" class="col-md-2 mb-2">
        <date-time-picker :value="fromDate" masks="DD-MM-YYYY HH:mm" @input="setFrom" />
      </div>
      <div class="col-auto mb-2 d-flex align-items-center">
        <input type="color" class="series-editor__color" :value="value.color" aria-label="Series color" @input="update({ color: $event.target.value })">
        <button type="button" class="btn btn-sm btn-outline-danger ml-2" @click="$emit('remove')">Remove</button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import DateTimePicker from '@/components/directives/DateTimePicker.vue';
import { CATALOG, pointsFor, channelModeOf } from '@/util/chart/customChartCatalog';
import { PERIOD_KINDS } from '@/util/chart/customChartModel';
import { discoverChannels } from '@/util/chart/customChartFetch';

export default {
  name: 'SeriesEditor',
  components: { DateTimePicker },
  props: {
    value: { type: Object, required: true },
    index: { type: Number, required: true },
    assets: { type: Array, default: () => [] },
    deviceNameByUid: { type: Object, default: null },
  },
  data() {
    return {
      periodKinds: PERIOD_KINDS,
      channels: [],
      recentChannels: true,
      loadingChannels: false,
    };
  },
  computed: {
    selectedAsset() {
      return this.assets.find((a) => String(a.massId) === String(this.value.massId)) || null;
    },
    devices() {
      return ((this.selectedAsset && this.selectedAsset.devices) || []).filter((d) => CATALOG[d.devcType]);
    },
    deviceMissing() {
      return !!this.value.deviceUid && !!this.deviceNameByUid
        && !Object.prototype.hasOwnProperty.call(this.deviceNameByUid, this.value.deviceUid);
    },
    points() {
      return pointsFor(this.value.deviceType);
    },
    channelMode() {
      return channelModeOf(this.value.deviceType);
    },
    fromDate() {
      return new Date(Number(this.value.period.startMs));
    },
  },
  watch: {
    'value.deviceUid': { immediate: true, handler: 'loadChannels' },
  },
  methods: {
    update(patch) {
      this.$emit('input', { ...this.value, ...patch });
    },
    // Nilai <select> native selalu string; dicocokin balik ke tipe aslinya.
    setAsset(raw) {
      const asset = this.assets.find((a) => String(a.massId) === String(raw));
      this.update({
        massId: asset ? asset.massId : null, deviceUid: '', deviceType: '', point: '', channel: null,
      });
    },
    setDevice(uid) {
      const device = this.devices.find((d) => d.devcUniqueId === uid);
      const points = pointsFor(device ? device.devcType : '');
      this.update({
        deviceUid: uid,
        deviceType: device ? device.devcType : '',
        point: points.length === 1 ? points[0].point : '',
        channel: null,
      });
    },
    setPeriod(kind) {
      this.update({
        period: kind === 'from'
          ? { kind, startMs: moment().subtract(7, 'days').startOf('day').valueOf() }
          : { kind },
      });
    },
    setFrom(date) {
      this.update({ period: { kind: 'from', startMs: moment(date).valueOf() } });
    },
    async loadChannels() {
      const uid = this.value.deviceUid;
      if (!this.channelMode || !uid) {
        this.channels = [];
        this.recentChannels = true;
        this.loadingChannels = false;
        return;
      }
      this.loadingChannels = true;
      try {
        const { channels, recent } = await discoverChannels(this.value.deviceType, uid, Date.now());
        // Device keburu diganti selagi nunggu.
        if (uid !== this.value.deviceUid) return;
        this.channels = this.value.channel && !channels.includes(this.value.channel)
          ? channels.concat(this.value.channel)
          : channels;
        this.recentChannels = recent;
        if (!this.value.channel && channels.length === 1) this.update({ channel: channels[0] });
      } finally {
        if (uid === this.value.deviceUid) this.loadingChannels = false;
      }
    },
  },
};
</script>

<style scoped>
.series-editor { display: flex; align-items: flex-start; gap: 8px; padding: 8px 0; border-bottom: 1px solid #eef0f3; }
.series-editor__no {
  width: 22px;
  height: 22px;
  margin-top: 4px;
  border-radius: 50%;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.series-editor__color { width: 32px; height: 30px; padding: 0; border: 1px solid #dee2e6; border-radius: 4px; background: transparent; }
</style>
